import { PlatformFeatureType } from '@conversed/shared/src/central/types/platform-features.type';
import { useCallback, useMemo } from 'react';
import { useCompanySelector } from './useCompanySelector';

export const usePlatformFeatures = () => {
  const { selectedCompany } = useCompanySelector();

  const platformFeatures = useMemo(
    () => selectedCompany?.platformFeatures ?? [],
    [selectedCompany?.platformFeatures],
  );

  const hasPlatformFeature = useCallback(
    (feature: PlatformFeatureType): boolean => {
      return platformFeatures.includes(feature);
    },
    [platformFeatures],
  );

  const hasLLMBlockFilteringForTrainAI = useMemo(
    () => hasPlatformFeature(PlatformFeatureType.LLMBlockFilteringForTrainAI),
    [hasPlatformFeature],
  );

  const hasUseAIKnowledgeLabels = useMemo(
    () => hasPlatformFeature(PlatformFeatureType.UseAIKnowledgeLabels),
    [hasPlatformFeature],
  );
  const hasAIKnowledgeHubV2 = useMemo(
    () => hasPlatformFeature(PlatformFeatureType.AIKnowledgeHubV2),
    [hasPlatformFeature],
  );

  const hasConversationKeywords = useMemo(
    () => hasPlatformFeature(PlatformFeatureType.ConversationKeywords),
    [hasPlatformFeature],
  );

  const hasHotTopicFilterForTrainAI = useMemo(
    () => hasPlatformFeature(PlatformFeatureType.HotTopicFilterForTrainAI),
    [hasPlatformFeature],
  );

  const hasWebclientVoice = useMemo(
    () => hasPlatformFeature(PlatformFeatureType.WebclientVoice),
    [hasPlatformFeature],
  );

  const hasTrainAIV2 = useMemo(
    () => hasPlatformFeature(PlatformFeatureType.TrainAIV2),
    [hasPlatformFeature],
  );

  const hasMultilingualTrainAI = useMemo(
    () => hasPlatformFeature(PlatformFeatureType.MultilingualTrainAI),
    [hasPlatformFeature],
  );

  const hasHomeV2 = useMemo(
    () => hasPlatformFeature(PlatformFeatureType.HomeV2),
    [hasPlatformFeature],
  );

  const hasHomeIntelligence = useMemo(
    () => hasPlatformFeature(PlatformFeatureType.HomeIntelligence),
    [hasPlatformFeature],
  );

  return {
    hasPlatformFeature,
    hasLLMBlockFilteringForTrainAI,
    hasUseAIKnowledgeLabels,
    hasAIKnowledgeHubV2,
    hasConversationKeywords,
    hasHotTopicFilterForTrainAI,
    hasWebclientVoice,
    hasTrainAIV2,
    hasMultilingualTrainAI,
    hasHomeV2,
    hasHomeIntelligence,
    platformFeatures,
  };
};
